import Alpine from 'alpinejs';
import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Keyboard, Autoplay } from 'swiper/modules';

// import styles bundle
import 'swiper/swiper.css';
import 'swiper/css/pagination';
import { AlpineComponentTryba } from '../../../scripts/alpine/include/global';

interface ProductPOIAlpine extends AlpineComponentTryba {
    swiper: Swiper | null;
    open: boolean;
    is768Max: boolean;
    activeIndex: number;
}


document.addEventListener('alpine:init', () => {
    Alpine.data('initHomePOI', (): ProductPOIAlpine => {
        return {
            swiper: null,
            open: false,
            isZoomed: false,
            is768Max: false,
            poiInViewport: false,
            activeIndex: 0,
            isProgrammatic: false,
            shown: false,
            isPlaying: true,
            _resizeHandler: null as (() => void) | null,
            imageRect: null as { offsetX: number; offsetY: number; displayedW: number; displayedH: number } | null,
            start() {
                this.is768Max = window.matchMedia('(max-width: 768px)').matches;

                this.setupImageTracking();
                let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
                this._resizeHandler = () => {
                    if (resizeTimeout) clearTimeout(resizeTimeout);
                    resizeTimeout = setTimeout(() => this.updateImageRect(), 150);
                };
                window.addEventListener('resize', this._resizeHandler);

                const swiperParams: SwiperOptions = {
                    modules: [Keyboard, Autoplay],
                    loop: false,
                    slidesPerView: 'auto',
                    slidesPerGroup: 1,
                    slideActiveClass: 'is-active',
                    slidesOffsetBefore: 8,
                    slidesOffsetAfter: 8,
                    breakpoints: {
                        768: {
                            slidesOffsetBefore: 24,
                            slidesOffsetAfter: 24,
                        },
                    },
                };

                if (this.is768Max) {
                    this.swiper = new Swiper(this.$refs.swiperContainerMobile, swiperParams);
                } else {
                   this.swiper = new Swiper(this.$refs.swiperContainer, swiperParams);
                }   

                this.$nextTick(() => {
                    (this.swiper as Swiper).init();
                });

                this.startAutoplay();

                // Anim POI a l'autoplay
                this.swiper.on('touchEnd', () => {
                    if (!this.isProgrammatic) {
                        if(this.swiper) {
                            this.activeIndex = this.swiper.realIndex;
                        }
                        if (this.isPlaying) this.startAutoplay();
                    }
                    this.isProgrammatic = false;
                });

            },

            startAutoplay() {
                if (!this.swiper) return;
                if (this.autoplayInterval) clearInterval(this.autoplayInterval);
                
                const activeSlide = document.querySelector('.poi-is-active');
                const poi = activeSlide?.querySelector('.js-animation-poi');
                
                
                if (poi && poi.classList.contains('animate-draw')) {
                     poi.classList.remove('animate-draw'); 
                    if (this.isPlaying) poi?.classList.add('animate-draw');
                }
                   
                
                
                this.isPlaying = true;

                const totalSlides = this.swiper.slides.length;

                this.autoplayInterval = setInterval(() => {
                    const nextIndex = this.activeIndex + 1;

                    this.isProgrammatic = true;
                    if (nextIndex >= totalSlides) {
                        this.activeIndex = 0;
                        this.swiper?.slideTo(0, 0);
                        setTimeout(() => {
                            if (this.swiper) { 
                                this.swiper.wrapperEl.style.transition = '';
                            }
                        }, 50);
                    } else {
                        this.activeIndex = nextIndex;
                        this.swiper?.slideTo(nextIndex, 600);
                    }
                }, 5000);
            },

            stopAutoplay() {
                if (this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                }
                const activeSlide = document.querySelector('.poi-is-active');
                const poi = activeSlide?.querySelector('.js-animation-poi');
                if (poi && poi.classList.contains('animate-draw')) poi.classList.remove('animate-draw');
                this.isPlaying = false;
            },

            togglePlayPause() {
                this.isPlaying ? this.stopAutoplay() : this.startAutoplay();
            },

            handleSlideTo(index: number) {
                if (!this.swiper) return;
                this.isProgrammatic = true;

                this.activeIndex = index;
                this.swiper.slideTo(index, 600);
                if (this.isPlaying) this.startAutoplay();
            },

            setupImageTracking() {
                const container = this.is768Max
                    ? this.$refs.poiContainerMobile : this.$refs.poiContainer;
                if (!container) return;
                const img = container.querySelector('img') as HTMLImageElement;
                if (!img) return;
                if (img.complete && img.naturalWidth) {
                    this.updateImageRect();
                } else {
                    img.addEventListener('load', () => this.updateImageRect(), { once: true });
                }
            },

            updateImageRect() {
                const container = this.is768Max
                    ? this.$refs.poiContainerMobile : this.$refs.poiContainer;
                if (!container) return;
                const img = container.querySelector('img') as HTMLImageElement;
                if (!img || !img.naturalWidth || !img.naturalHeight) return;

                const containerW = container.clientWidth;
                const containerH = container.clientHeight;
                if (!containerH) return;

                const imageRatio = img.naturalWidth / img.naturalHeight;
                const containerRatio = containerW / containerH;

                let displayedW: number, displayedH: number, offsetX: number, offsetY: number;
                if (imageRatio > containerRatio) {
                    displayedH = containerH;
                    displayedW = containerH * imageRatio;
                    offsetX = -(displayedW - containerW) / 2;
                    offsetY = 0;
                } else {
                    displayedW = containerW;
                    displayedH = containerW / imageRatio;
                    offsetX = 0;
                    offsetY = -(displayedH - containerH) / 2;
                }
                this.imageRect = { offsetX, offsetY, displayedW, displayedH };
            },

            computePointPosition(origTop: number, origLeft: number): string {
                if (!this.imageRect) {
                    return `top: ${origTop}%; left: ${origLeft}%`;
                }
                const container = this.is768Max
                    ? this.$refs.poiContainerMobile : this.$refs.poiContainer;
                if (!container) return `top: ${origTop}%; left: ${origLeft}%`;

                const containerW = container.clientWidth;
                const containerH = container.clientHeight;
                const { offsetX, offsetY, displayedW, displayedH } = this.imageRect;

                const left = (origLeft / 100) * displayedW + offsetX;
                const top = (origTop / 100) * displayedH + offsetY;

                return `top: ${(top / containerH) * 100}%; left: ${(left / containerW) * 100}%`;
            },

            destroy() {
                if (this._resizeHandler) {
                    window.removeEventListener('resize', this._resizeHandler);
                }
            },
        };
    });
});
